import { useEffect, useRef, useState } from 'react';

export type AdPlacement = 'header' | 'footer' | 'sidebar' | 'content' | 'popunder';

interface AdBannerProps {
  placement: AdPlacement;
  className?: string;
}

const AD_CONFIG: Record<AdPlacement, { width: string; height: string; label: string }> = {
  header:   { width: '728px',  height: '90px',  label: 'Header Banner' },
  footer:   { width: '728px',  height: '90px',  label: 'Footer Banner' },
  sidebar:  { width: '300px',  height: '250px', label: 'Sidebar Banner' },
  content:  { width: '728px',  height: '90px',  label: 'Content Banner' },
  popunder: { width: '0',     height: '0',     label: '' },
};

const PLACEMENT_KEY: Record<AdPlacement, string> = {
  header: 'ad_header',
  footer: 'ad_footer',
  sidebar: 'ad_sidebar',
  content: 'ad_content',
  popunder: 'ad_popunder',
};

const PLACEMENT_ENABLED: Record<AdPlacement, string> = {
  header: 'ad_header_enabled',
  footer: 'ad_footer_enabled',
  sidebar: 'ad_sidebar_enabled',
  content: 'ad_content_enabled',
  popunder: 'ad_popunder_enabled',
};

let settingsCache: Record<string, string> | null = null;
let settingsPromise: Promise<Record<string, string>> | null = null;

async function fetchAdSettings(): Promise<Record<string, string>> {
  if (settingsCache) return settingsCache;
  if (settingsPromise) return settingsPromise;

  settingsPromise = (async () => {
    try {
      const res = await fetch('/api/settings');
      if (res.ok) {
        settingsCache = await res.json();
      }
    } catch {}
    settingsCache = settingsCache || {};
    return settingsCache;
  })();

  return settingsPromise;
}

function AdBannerInner({ placement, className = '' }: AdBannerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [adCode, setAdCode] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchAdSettings().then((settings) => {
      if (cancelled) return;
      const key = PLACEMENT_KEY[placement];
      const enabledKey = PLACEMENT_ENABLED[placement];
      const isEnabled = settings[enabledKey] === 'true';
      setEnabled(isEnabled);
      setAdCode(isEnabled ? (settings[key] || '') : '');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [placement]);

  useEffect(() => {
    if (!ref.current || !adCode) return;
    ref.current.innerHTML = adCode;
    const scripts = ref.current.querySelectorAll('script');
    scripts.forEach((old) => {
      const s = document.createElement('script');
      s.text = old.textContent || '';
      if (old.src) s.src = old.src;
      Array.from(old.attributes).forEach((attr) => s.setAttribute(attr.name, attr.value));
      old.parentNode?.replaceChild(s, old);
    });
  }, [adCode]);

  if (loading || !enabled || !adCode) return null;

  const config = AD_CONFIG[placement];
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const mobileHeight = placement === 'header' || placement === 'content' ? '50px' : config.height;

  if (placement === 'popunder') {
    return <div ref={ref} />;
  }

  return (
    <div
      ref={ref}
      className={`ad-banner ad-banner--${placement} flex items-center justify-center overflow-hidden ${className}`}
      style={{
        width: isMobile ? '320px' : config.width,
        height: isMobile ? mobileHeight : config.height,
        maxWidth: '100%',
      }}
    />
  );
}

export default function AdBanner(props: AdBannerProps) {
  return <AdBannerInner {...props} />;
}

export function loadAnalyticsScripts() {
  fetchAdSettings().then((settings) => {
    if (settings.histat_enabled === 'true' && settings.histat_script && !document.getElementById('histat-script')) {
      const div = document.createElement('div');
      div.id = 'histat-script';
      div.innerHTML = settings.histat_script;
      document.head.appendChild(div);
      div.querySelectorAll('script').forEach((old) => {
        const s = document.createElement('script');
        s.text = old.textContent || '';
        if (old.src) s.src = old.src;
        Array.from(old.attributes).forEach((attr) => s.setAttribute(attr.name, attr.value));
        old.parentNode?.replaceChild(s, old);
      });
    }

    if (settings.google_console_enabled === 'true' && settings.google_console_meta && !document.getElementById('gsc-meta')) {
      const div = document.createElement('div');
      div.id = 'gsc-meta';
      div.innerHTML = settings.google_console_meta;
      document.head.appendChild(div);
    }
  });
}
